import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileText, Clock, ChevronRight, Trash2, Copy, Zap, Target, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import nexisLogo from "@/assets/nexis-logo-new.png";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Project {
  id: string;
  title: string;
  last_step: string;
  updated_at: string;
  created_at: string;
  script: string | null;
}

interface SceneInfo {
  project_id: string;
  has_image_prompts: boolean;
}

const stepLabels: Record<string, string> = {
  script: "Script",
  "visual-style": "Visual Style",
  "scene-count": "Scene Count",
  "image-prompts": "Image Prompts",
  "video-prompts": "Video Prompts",
};

type ProjectStatus = "draft" | "edited" | "complete";

function getProjectStatus(project: Project, hasScenes: boolean): ProjectStatus {
  if (project.last_step === "video-prompts" || project.last_step === "image-prompts") {
    return "complete";
  }
  if (project.last_step !== "script" || (project.script && project.script.length > 100)) {
    return "edited";
  }
  return "draft";
}

const statusConfig: Record<ProjectStatus, { label: string; dotClass: string }> = {
  draft: { label: "Draft", dotClass: "bg-muted-foreground" },
  edited: { label: "Edited", dotClass: "bg-blue-500" },
  complete: { label: "Complete", dotClass: "bg-green-500" },
};

export default function NexisDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [sceneMap, setSceneMap] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchProjects();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from("nexis_projects")
        .select("id, title, last_step, updated_at, created_at, script")
        .order("updated_at", { ascending: false });

      if (error) throw error;
      setProjects(data || []);

      // Fetch which projects have scenes
      if (data && data.length > 0) {
        const ids = data.map((p) => p.id);
        const { data: scenes } = await supabase
          .from("nexis_scenes")
          .select("project_id")
          .in("project_id", ids)
          .limit(1000);
        
        const map: Record<string, boolean> = {};
        scenes?.forEach((s) => { map[s.project_id] = true; });
        setSceneMap(map);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleProjectClick = (project: Project) => {
    const stepRoutes: Record<string, string> = {
      script: `/app/nexis-ai/project/${project.id}/script`,
      "visual-style": `/app/nexis-ai/project/${project.id}/visual-style`,
      "scene-count": `/app/nexis-ai/project/${project.id}/scene-count`,
      "image-prompts": `/app/nexis-ai/project/${project.id}/image-prompts`,
      "video-prompts": `/app/nexis-ai/project/${project.id}/video-prompts`,
    };
    navigate(stepRoutes[project.last_step] || `/app/nexis-ai/project/${project.id}/script`);
  };

  const handleDeleteProject = async (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { error } = await supabase
        .from("nexis_projects")
        .delete()
        .eq("id", projectId);

      if (error) throw error;
      setProjects(projects.filter((p) => p.id !== projectId));
      toast.success("Project deleted");
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    }
  };

  const handleDuplicateProject = async (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;

    try {
      const { data: originalProject, error: projectError } = await supabase
        .from("nexis_projects")
        .select("*")
        .eq("id", projectId)
        .single();

      if (projectError) throw projectError;

      const { data: newProject, error: insertError } = await supabase
        .from("nexis_projects")
        .insert({
          user_id: user.id,
          title: `${originalProject.title} (Copy)`,
          script: originalProject.script,
          niche: originalProject.niche,
          platform: originalProject.platform,
          tone: originalProject.tone,
          length: originalProject.length,
          prompt_type: originalProject.prompt_type,
          visual_style: originalProject.visual_style,
          visual_style_category: originalProject.visual_style_category,
          recommended_model: originalProject.recommended_model,
          secondary_model: originalProject.secondary_model,
          scene_count: originalProject.scene_count,
          idea_data: originalProject.idea_data,
          last_step: originalProject.last_step,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      const { data: scenes, error: scenesError } = await supabase
        .from("nexis_scenes")
        .select("*")
        .eq("project_id", projectId);

      if (!scenesError && scenes && scenes.length > 0) {
        const duplicatedScenes = scenes.map((scene) => ({
          project_id: newProject.id,
          scene_number: scene.scene_number,
          description: scene.description,
          duration: scene.duration,
          image_prompt: scene.image_prompt,
          video_prompt: scene.video_prompt,
        }));

        await supabase.from("nexis_scenes").insert(duplicatedScenes);
      }

      await fetchProjects();
      toast.success("Project duplicated");
    } catch (error) {
      console.error("Error duplicating project:", error);
      toast.error("Failed to duplicate project");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <div className="flex items-center gap-3 md:gap-4">
            <img src={nexisLogo} alt="Nexis AI" className="w-10 h-10 md:w-12 md:h-12 object-contain flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="font-display text-2xl md:text-3xl font-bold">Nexis AI</h1>
              <p className="text-sm md:text-base text-muted-foreground truncate">
                Research, ideas, scripting and planning
              </p>
            </div>
          </div>
          <Button
            variant="hero"
            size="default"
            onClick={() => navigate("/app/nexis-ai/create")}
            className="gap-2 w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 md:w-5 md:h-5" />
            New Script
          </Button>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-32 md:h-40 rounded-xl" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="max-w-2xl mx-auto">
            <Card className="p-8 md:p-12 text-center bg-card/50 border-border/50 mb-6">
              <FileText className="w-12 h-12 md:w-16 md:h-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl md:text-2xl font-display font-bold mb-2">Write your first script</h2>
              <p className="text-sm md:text-base text-muted-foreground mb-6">
                Nexis AI writes scroll-stopping scripts, video ideas, and promotional content for any platform — in seconds.
              </p>
              <Button
                variant="hero"
                size="default"
                onClick={() => navigate("/app/nexis-ai/create")}
                className="gap-2"
              >
                <Plus className="w-4 h-4 md:w-5 md:h-5" />
                New Script
              </Button>
            </Card>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Card
                className="p-4 bg-card/50 border-border/50 hover:border-primary/50 cursor-pointer transition-all text-center"
                onClick={() => navigate("/app/nexis-ai/create")}
              >
                <Zap className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-sm font-semibold text-foreground">TikTok script for my niche</p>
                <p className="text-xs text-muted-foreground mt-1">Short-form viral content</p>
              </Card>
              <Card
                className="p-4 bg-card/50 border-border/50 hover:border-primary/50 cursor-pointer transition-all text-center"
                onClick={() => navigate("/app/nexis-ai/create")}
              >
                <Target className="w-6 h-6 text-secondary mx-auto mb-2" />
                <p className="text-sm font-semibold text-foreground">Promotional script for my product</p>
                <p className="text-xs text-muted-foreground mt-1">Sales Content Mode</p>
              </Card>
              <Card
                className="p-4 bg-card/50 border-border/50 hover:border-primary/50 cursor-pointer transition-all text-center"
                onClick={() => navigate("/app/nexis-ai/create")}
              >
                <Lightbulb className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                <p className="text-sm font-semibold text-foreground">Generate YouTube video ideas</p>
                <p className="text-xs text-muted-foreground mt-1">Long-form content planning</p>
              </Card>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {projects.map((project) => {
              const status = getProjectStatus(project, !!sceneMap[project.id]);
              const cfg = statusConfig[status];
              return (
                <Card
                  key={project.id}
                  className="p-4 md:p-5 bg-card/50 border-border/50 hover:border-primary/50 active:border-primary/50 transition-all cursor-pointer group relative"
                  onClick={() => handleProjectClick(project)}
                >
                  <div className="flex items-start justify-between mb-2 md:mb-3">
                    <div className="flex-1 min-w-0 pr-2">
                      <h3 className="font-semibold text-base md:text-lg truncate group-hover:text-primary transition-colors">
                        {project.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground mt-1">
                        <Clock className="w-3 h-3 md:w-3.5 md:h-3.5" />
                        <span>{formatDate(project.updated_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity h-8 w-8 flex-shrink-0"
                        onClick={(e) => handleDuplicateProject(project.id, e)}
                        title="Duplicate project"
                      >
                        <Copy className="w-4 h-4 text-muted-foreground hover:text-primary" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity h-8 w-8 flex-shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="max-w-[90vw] sm:max-w-lg">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Project</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{project.title}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                            <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={(e) => handleDeleteProject(project.id, e)}
                              className="bg-destructive hover:bg-destructive/90 w-full sm:w-auto"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 md:px-2.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                        {stepLabels[project.last_step] || project.last_step}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-[10px] md:text-xs text-muted-foreground">
                        <span className={`w-2 h-2 rounded-full ${cfg.dotClass}`} />
                        {cfg.label}
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
